import About from "@/common/pages/About.vue";
import Vue from "vue";
import VueRouter, { NavigationGuardNext, Route, RouteConfig } from "vue-router";
import { authService } from "@/common/services/AuthService";
import Dashboard from "@/common/pages/Dashboard.vue";
import PurchaseReports from "@/purchase-reports/pages/PurchaseReports.vue";
import UserAccounts from "@/user-accounts/pages/UserAccounts.vue";
import Stores from "@/stores/pages/Stores.vue";
import Receipts from "@/receipts/pages/Receipts.vue";
import StoreReceipts from "@/receipts/pages/StoreReceipts.vue";
import Products from "@/products/pages/Products.vue";
import DataUploader from "@/data-uploader/pages/DataUploader.vue";
import Offers from "@/offers/pages/Offers.vue";
import Allergens from "@/allergens/pages/Allergens.vue";
import SeasonalHeader from "@/seasonal-header/pages/SeasonalHeader.vue";
import ProductLocation from "@/product-locations/pages/ProductLocation.vue";
import PPOrderList from "@/picking-portal/pages/PPOrderList.vue";
import PPPickOrder from "@/picking-portal/pages/PPPickOrder.vue";
import { Paths, RouteInfoMap } from "@/common/enums/RouterPaths";
import UAUserList from "@/user-administration/pages/UAUserList.vue";
import { accessService } from "@/common/services/AccessService";
import PPSlotBookings from "@/picking-portal/pages/PPSlotBookings.vue";
import PPCollectOrder from "@/picking-portal/pages/PPCollectOrder.vue";
import AisleCategory from "@/aisle-category/pages/AisleCategory.vue";
import AisleCategoryEdit from "@/aisle-category/pages/AisleCategoryEdit.vue";

Vue.use(VueRouter);

const routes = [
  {
    path: RouteInfoMap.get(Paths.Dashboard)?.path,
    name: RouteInfoMap.get(Paths.Dashboard)?.name,
    component: Dashboard,
  },
  {
    path: RouteInfoMap.get(Paths.PurchaseReports)?.path,
    name: RouteInfoMap.get(Paths.PurchaseReports)?.name,
    component: PurchaseReports,
  },
  {
    path: RouteInfoMap.get(Paths.UserAccounts)?.path,
    name: RouteInfoMap.get(Paths.UserAccounts)?.name,
    component: UserAccounts,
  },
  {
    ...RouteInfoMap.get(Paths.Stores),
    component: Stores,
    props: (route): object => {
      const storeId = route.query.storeId as string;
      return {
        storeId: storeId ? parseInt(storeId) : 0,
      };
    },
    meta: {
      async allowed(): Promise<boolean> {
        return authService.isLoggedIn() && accessService.hasAccessToStores();
      },
    },
  },
  {
    ...RouteInfoMap.get(Paths.Receipts),
    component: Receipts,
    props: (route): object => {
      const storeId = route.query.storeId as string;
      return {
        storeId: storeId ? parseInt(storeId) : 0,
      };
    },
    meta: {
      async allowed(): Promise<boolean> {
        return authService.isLoggedIn() && accessService.hasAccessToStores();
      },
    },
  },
  {
    ...RouteInfoMap.get(Paths.StoreReceipts),
    component: StoreReceipts,
    props: (route): object => {
      const storeId = route.query.storeId as string;
      return {
        storeId: storeId ? parseInt(storeId) : 0,
      };
    },
    meta: {
      async allowed(): Promise<boolean> {
        return authService.isLoggedIn() && accessService.hasAccessToStores();
      },
    },
  },
  {
    path: RouteInfoMap.get(Paths.Products)?.path,
    name: RouteInfoMap.get(Paths.Products)?.name,
    component: Products,
  },
  {
    path: RouteInfoMap.get(Paths.DataUploader)?.path,
    name: RouteInfoMap.get(Paths.DataUploader)?.name,
    component: DataUploader,
  },
  {
    path: RouteInfoMap.get(Paths.Offers)?.path,
    name: RouteInfoMap.get(Paths.Offers)?.name,
    component: Offers,
  },
  {
    path: RouteInfoMap.get(Paths.Allergens)?.path,
    name: RouteInfoMap.get(Paths.Allergens)?.name,
    component: Allergens,
  },
  {
    path: RouteInfoMap.get(Paths.SeasonalHeader)?.path,
    name: RouteInfoMap.get(Paths.SeasonalHeader)?.name,
    component: SeasonalHeader,
  },
  {
    path: RouteInfoMap.get(Paths.ProductLocation)?.path,
    name: RouteInfoMap.get(Paths.ProductLocation)?.name,
    component: ProductLocation,
  },
  {
    ...RouteInfoMap.get(Paths.ClickAndCollect),
    component: PPSlotBookings,
    props: (route): object => {
      const storeId = route.query.storeId as string;
      return {
        storeId: storeId ? parseInt(storeId) : 0,
        date: route.query.date,
      };
    },
    meta: {
      async allowed(): Promise<boolean> {
        return authService.isLoggedIn() && accessService.hasAccessToPickingPortal();
      },
      breadCrumb(data: any): Array<object> {
        return [
          {
            text: "Timeslots",
            disabled: true,
            to: {
              name: RouteInfoMap.get(Paths.ClickAndCollect)?.name,
              query: {
                storeId: data.storeId,
                date: data.date,
              },
            },
          },
        ];
      },
    },
  },
  {
    ...RouteInfoMap.get(Paths.UserAdministration),
    component: UAUserList,
    meta: {
      async allowed(): Promise<boolean> {
        return authService.isLoggedIn() && accessService.hasAccessToUserAdministration();
      },
    },
  },
  {
    ...RouteInfoMap.get(Paths.ClickAndCollectOrders),
    component: PPOrderList,
    props: (route): object => {
      const storeId = route.query.storeId as string;
      return {
        storeId: storeId ? parseInt(storeId) : 0,
        from: route.query.from,
        to: route.query.to,
      };
    },
    meta: {
      breadCrumb(data: any): Array<object> {
        return [
          {
            text: "Timeslots",
            disabled: false,
            to: {
              name: RouteInfoMap.get(Paths.ClickAndCollect)?.name,
              query: {
                storeId: data.storeId,
                date: data.date,
              },
            },
          },
          {
            text: "Orders",
            disabled: true,
          },
        ];
      },
    },
  },
  {
    ...RouteInfoMap.get(Paths.ClickAndCollectPickOrder),
    component: PPPickOrder,
    props: (route): object => {
      const transactionId = route.params.transactionId as string;
      return {
        transactionId: transactionId ? transactionId : "not found",
      };
    },
    meta: {
      breadCrumb(data: any): Array<object> {
        return [
          {
            text: "Timeslots",
            disabled: false,
            to: {
              name: RouteInfoMap.get(Paths.ClickAndCollect)?.name,
              query: {
                storeId: data.storeId,
                date: data.date,
              },
            },
          },
          {
            text: "Orders",
            disabled: false,
            to: {
              name: RouteInfoMap.get(Paths.ClickAndCollectOrders)?.name,
              query: {
                storeId: data.storeId ? data.storeId : 0,
                from: data.from,
                to: data.to,
              },
            },
          },
          {
            text: "Pick",
            disabled: true,
          },
        ];
      },
    },
  },
  {
    ...RouteInfoMap.get(Paths.ClickAndCollectCollectOrder),
    component: PPCollectOrder,
    props: (route): object => {
      const transactionId = route.params.transactionId as string;
      return {
        transactionId: transactionId ? transactionId : "not found",
      };
    },
    meta: {
      breadCrumb(data: any): Array<object> {
        return [
          {
            text: "Timeslots",
            disabled: false,
            to: {
              name: RouteInfoMap.get(Paths.ClickAndCollect)?.name,
              query: {
                storeId: data.storeId,
                date: data.date,
              },
            },
          },
          {
            text: "Orders",
            disabled: false,
            to: {
              name: RouteInfoMap.get(Paths.ClickAndCollectOrders)?.name,
              query: {
                storeId: data.storeId ? data.storeId : 0,
                from: data.from,
                to: data.to,
              },
            },
          },
          {
            text: "Collect",
            disabled: true,
          },
        ];
      },
    },
  },
  {
    ...RouteInfoMap.get(Paths.AisleCategory),
    component: AisleCategory,
    meta: {
      async allowed(): Promise<boolean> {
        return authService.isLoggedIn() && accessService.hasAccessToAisleCategory();
      },
    },
  },
  {
    ...RouteInfoMap.get(Paths.AisleCategoryAdd),
    component: AisleCategoryEdit,
    props: {
      add: true,
    },
    meta: {
      async allowed(): Promise<boolean> {
        return authService.isLoggedIn() && accessService.hasAccessToAisleCategory();
      },
    },
  },
  {
    ...RouteInfoMap.get(Paths.AisleCategoryEdit),
    component: AisleCategoryEdit,
    props: (route): object => ({ edit: true, aisleCategoryId: parseInt(route.params.aisleCategoryId) }),
    meta: {
      async allowed(): Promise<boolean> {
        return authService.isLoggedIn() && accessService.hasAccessToAisleCategory();
      },
    },
  },
  {
    path: RouteInfoMap.get(Paths.About)?.path,
    name: RouteInfoMap.get(Paths.About)?.name,
    component: About,
    meta: {
      async allowed(): Promise<boolean> {
        // ? This function can execute logic to check if the user is
        // ? allowed to enter the route, e.g. is logged in, has correct permissions or other attributes.
        return authService.isLoggedIn();
      },
    },
  },
] as RouteConfig[];

/**
 *
 /* @param {import("vue-router").Route} to
 /* @param {import("vue-router").Route} from
 /* @param {import("vue-router").NavigationGuardNext} next
 */
export async function checkAllowedNavigationGuard(to: Route, from: Route, next: NavigationGuardNext): Promise<void> {
  const toMeta = to?.meta ?? {};
  const allowedFunction = toMeta?.allowed;

  // ? There's an allowed function, execute it asynchronously and check if the result is truthy.
  if (typeof allowedFunction === "function") {
    const isAllowed = await allowedFunction(to, from);

    if (isAllowed) {
      next();
    } else {
      next("/");
    }
    // ? There's no allowed function, continue navigation normally.
  } else next();
}

export const AppRouter = new VueRouter({
  mode: "history",
  routes,
});

AppRouter.beforeEach(checkAllowedNavigationGuard);
